import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Box, Typography, Button, Avatar, Chip, Stack, Tooltip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField, InputAdornment,
    Snackbar, Alert, MenuItem, LinearProgress, CircularProgress, Switch, Autocomplete,
} from '@mui/material';
import AccessTimeFilledRoundedIcon from '@mui/icons-material/AccessTimeFilledRounded';
import LayersRoundedIcon from '@mui/icons-material/LayersRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { PRIMARY, PRIMARY_LIGHT, PRIMARY_DARK } from '../theme';
import { inr, initialsFromName as initials, cleanName, avatarTone } from '../utils/format';
import http, { apiErrorMessage } from '../Api/http';
import { LoadAlert } from '../components/uiKit';
import SecretDialog from '../components/SecretDialog';
import { passphraseConfig, isPassphraseError, passphraseErrorText } from '../utils/salaryPassphrase';
import { useCrumbActionsSetter } from '../utils/crumbActions';
import {
    GetOvertimeDashboard, AddOvertimeRateSlab, UpdateOvertimeRateSlab, DeleteOvertimeRateSlab, UpdateSundayOvertime,
    UpdateOvertimeCountsAfter, UpdateOvertimeEntryAction, PostOvertimeEntry, GetEmployees,
} from '../Api/Api';

const card = { bgcolor: '#fff', border: '1px solid #E6EAF1', borderRadius: '7px', boxShadow: '0 1px 3px rgba(16,24,40,0.06)' };
const solidBtn = { bgcolor: PRIMARY, color: '#fff', fontWeight: 700, borderRadius: '7px', boxShadow: 'none', textTransform: 'none', '&:hover': { bgcolor: PRIMARY_DARK, boxShadow: 'none' } };

const fmtHrs = (h) => {
    const n = Number(h) || 0;
    return `${Number.isInteger(n) ? n : Number(n.toFixed(2))} hr${n === 1 ? '' : 's'}`;
};
const slabRange = (s) => (s.toHr === null ? `${s.fromHr}+ hrs` : `${s.fromHr}–${s.toHr} hrs`);

const STD_MONTHLY_HOURS = 26 * 8;
const STD_WORKING_DAYS = 26;
const hourlyFromMonthly = (monthly) => (Number(monthly) || 0) / STD_MONTHLY_HOURS;
const dailyFromMonthly = (monthly) => (Number(monthly) || 0) / STD_WORKING_DAYS;

const MIN_OT_PCT = 100;
const MAX_OT_PCT = 300;

const SAMPLE_MONTHLY = 200000;

const WEEKOFF_PAY_OPTIONS = [
    { key: 'slabs', label: 'Use the OT rate slabs below', noValue: true },
    { key: 'salarypct', label: '% of hourly salary', unit: '%' },
    { key: 'perhour', label: 'Flat ₹ per hour', unit: '₹', prefix: true },
    { key: 'fulldaysalary', label: 'Full-day salary', noValue: true },
    { key: 'halfdaysalary', label: 'Half-day salary', noValue: true },
];
const DEFAULT_WEEKOFF = { enabled: false, days: [0], payType: 'salarypct', rate: 200 };
const weekOffPayOption = (k) => WEEKOFF_PAY_OPTIONS.find((o) => o.key === k) || WEEKOFF_PAY_OPTIONS[0];

const OT_PAYTYPE_TO_API = { perhour: 'perhour', flat: 'flat', salarypct: 'percenthourly', halfdaysalary: 'halfday', fulldaysalary: 'fullday' };
const OT_PAYTYPE_FROM_API = { perhour: 'perhour', flat: 'flat', percenthourly: 'salarypct', halfday: 'halfdaysalary', fullday: 'fulldaysalary' };
const WEEKOFF_TO_API = { slabs: 'slabs', ...OT_PAYTYPE_TO_API };
const WEEKOFF_FROM_API = { slabs: 'slabs', ...OT_PAYTYPE_FROM_API };
const normalizeWeekOff = (w) => {
    const raw = String(w?.payBasis ?? w?.PayBasis ?? w?.payType ?? w?.PayType ?? DEFAULT_WEEKOFF.payType).toLowerCase();
    const payType = WEEKOFF_FROM_API[raw] || (WEEKOFF_PAY_OPTIONS.some((o) => o.key === raw) ? raw : DEFAULT_WEEKOFF.payType);
    return {
        enabled: Boolean(w?.enabled ?? w?.Enabled),
        days: DEFAULT_WEEKOFF.days,
        payType,
        rate: Number(w?.rate ?? w?.Rate ?? DEFAULT_WEEKOFF.rate) || DEFAULT_WEEKOFF.rate,
    };
};

const PAY_META = {
    perhour: { label: 'Per hour', bg: '#DCFCE7', color: '#15803D' },
    flat: { label: 'Flat amount', bg: '#FFF7ED', color: '#B45309' },
    salarypct: { label: '% of salary', bg: PRIMARY_LIGHT, color: PRIMARY_DARK },
    halfdaysalary: { label: 'Half-day salary', bg: '#E0F2FE', color: '#0369A1' },
    fulldaysalary: { label: 'Full-day salary', bg: '#E0F2FE', color: '#0369A1' },
};
const payMeta = (pt) => PAY_META[pt] || PAY_META.perhour;

const slabPayAt = (s, hours, monthly = 0) => {
    if (s.payType === 'flat') return Math.round(s.amount);
    if (s.payType === 'fulldaysalary') return Math.round(dailyFromMonthly(monthly));
    if (s.payType === 'halfdaysalary') return Math.round(dailyFromMonthly(monthly) / 2);
    if (s.payType === 'salarypct') return Math.round(hourlyFromMonthly(monthly) * (s.amount / 100) * hours);
    return Math.round(s.amount * hours);
};

const pick = (o, ...keys) => { for (const k of keys) if (o?.[k] !== undefined && o[k] !== null) return o[k]; return undefined; };
const normalizeSlab = (s) => {
    const toRaw = pick(s, 'toHours', 'ToHours', 'toHr');
    const andAbove = Boolean(pick(s, 'andAbove', 'AndAbove') ?? toRaw === undefined);
    return {
        id: pick(s, 'id', 'Id'),
        label: pick(s, 'name', 'Name', 'label') ?? '',
        fromHr: Number(pick(s, 'fromHours', 'FromHours', 'fromHr') ?? 0),
        toHr: andAbove || toRaw === undefined ? null : Number(toRaw),
        payType: (() => {
            const pt = String(pick(s, 'payType', 'PayType') ?? 'perhour').toLowerCase();
            if (pt === 'flat') return 'flat';
            if (['salarypct', 'salarypercent', 'percent', 'hourlypct', 'percenthourly'].includes(pt)) return 'salarypct';
            if (['fulldaysalary', 'fullday', 'daysalary'].includes(pt)) return 'fulldaysalary';
            if (['halfdaysalary', 'halfday'].includes(pt)) return 'halfdaysalary';
            return 'perhour';
        })(),
        amount: Number(pick(s, 'rate', 'Rate', 'amount') ?? 0),
    };
};

const normalizeEntry = (e) => ({
    id: e.id,
    employeeCode: e.employeeCode ?? '',
    employeeName: e.employeeName ?? '',
    department: e.department ?? '',
    date: e.date ?? '',
    shiftEnd: e.shiftEnd ?? '',
    signOff: e.signOff ?? '',
    otHours: Number(e.otHours) || 0,
    slabName: e.slabName ?? '',
    otPay: Number(e.otPay) || 0,
    status: String(e.status ?? '').toLowerCase(),
    rejectReason: e.rejectReason ?? null,
    // Salary-based pay (percenthourly / halfday / fullday) is recomputed from the real salary on approval.
    isSalaryBased: e.isSalaryBased ?? ['percenthourly', 'halfday', 'fullday'].includes(String(e.payType ?? '').toLowerCase()),
});

const EMPTY_DASH = {
    rateSlabs: 0, topHourlyRate: 0, otHoursLogged: 0, pendingApprovals: 0,
    countsAfterMinutes: 0, slabs: [], entries: [],
};
const EMPTY_SLAB = { label: '', fromHr: '', toHr: '', andAbove: false, payType: 'perhour', amount: '' };

const MAX_GRACE_MIN = 240;

const OT_REJECT_TEMPLATES = [
    { label: 'Not pre-approved', text: 'This overtime was not authorised in advance by your reporting manager, so it cannot be approved.' },
    { label: 'Not enough justification', text: 'The reason given does not justify overtime for this date. Please add detail and raise it again.' },
    { label: 'Within grace period', text: 'The extra time falls within the allowed grace window past shift end and does not qualify as overtime.' },
    { label: 'Sign-off looks wrong', text: 'The sign-off time appears incorrect. Please verify your punch-out with your manager and raise the entry again.' },
    { label: 'No work assigned', text: 'No assigned task required you to stay beyond shift end on this date.' },
    { label: 'Already recorded', text: 'Overtime for this date has already been recorded, so this entry is a duplicate.' },
];

const coverageEnd = (list) => list.reduce((max, s) => (s.toHr === null ? max : Math.max(max, s.toHr)), 0);
const hasOpenEndedBand = (list) => list.some((s) => s.toHr === null);
const bandAbove = (list, from) => list.filter((s) => s.fromHr > from).sort((a, b) => a.fromHr - b.fromHr)[0] || null;

const pad2 = (n) => String(n).padStart(2, '0');
const todayIso = () => {
    const d = new Date();
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const EMPTY_OT = { employee: null, date: '', signOff: '' };

export default function OvertimePage() {
    const [dash, setDash] = useState(EMPTY_DASH);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deletingId, setDeletingId] = useState(null);
    const [loadError, setLoadError] = useState('');

    const [snack, setSnack] = useState(null);
    const [dialog, setDialog] = useState(null);
    const [confirm, setConfirm] = useState(null);
    const [form, setForm] = useState(EMPTY_SLAB);
    const [tried, setTried] = useState(false);

    const [grace, setGrace] = useState('');
    const [savingGrace, setSavingGrace] = useState(false);

    const [weekOff, setWeekOff] = useState(DEFAULT_WEEKOFF);
    const [savedWeekOff, setSavedWeekOff] = useState(DEFAULT_WEEKOFF);
    const [savingWeekOff, setSavingWeekOff] = useState(false);

    const [actingId, setActingId] = useState(null);
    const [reject, setReject] = useState(null);
    const [rejectReason, setRejectReason] = useState('');

    const notify = (msg, sev = 'success') => setSnack({ msg, sev });

    const [otDialog, setOtDialog] = useState(false);
    const [otForm, setOtForm] = useState(EMPTY_OT);
    const [otTried, setOtTried] = useState(false);
    const [otSaving, setOtSaving] = useState(false);
    const [otError, setOtError] = useState('');
    const [employees, setEmployees] = useState([]);
    const [employeesLoading, setEmployeesLoading] = useState(false);

    const loadDashboard = useCallback(async () => {
        setLoading(true);
        try {
            const { data: body } = await http.get(GetOvertimeDashboard);
            if (body?.error) throw new Error(body.message || 'Could not load the overtime dashboard.');
            const d = body?.data || {};
            setDash({
                rateSlabs: Number(d.rateSlabs) || 0,
                topHourlyRate: Number(d.topHourlyRate) || 0,
                otHoursLogged: Number(d.otHoursLogged) || 0,
                pendingApprovals: Number(d.pendingApprovals) || 0,
                countsAfterMinutes: Number(d.countsAfterMinutes) || 0,
                slabs: (Array.isArray(d.slabs) ? d.slabs : []).map(normalizeSlab).sort((a, b) => a.fromHr - b.fromHr),
                entries: (Array.isArray(d.entries) ? d.entries : []).map(normalizeEntry),
            });
            const wo = normalizeWeekOff(d.sundayOvertime || d.weeklyOffOt || d.weekendOt);
            setWeekOff(wo);
            setSavedWeekOff(wo);
            setLoadError('');
        } catch (err) {
            setLoadError(apiErrorMessage(err, 'Could not load the overtime dashboard.'));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadDashboard(); }, [loadDashboard]);

    const openOtDialog = async () => {
        setOtForm({ ...EMPTY_OT, date: todayIso() });
        setOtTried(false);
        setOtError('');
        setOtDialog(true);
        if (employees.length || employeesLoading) return;
        setEmployeesLoading(true);
        try {
            const { data: body } = await http.get(GetEmployees, { params: { search: '', page: 1, pageSize: 1000 } });
            const items = body?.data?.items || [];
            setEmployees(items.filter((r) => r.employeeCode && String(r.status || 'active').toLowerCase() === 'active'));
        } catch (err) {
            setOtError(apiErrorMessage(err, 'Could not load employees.'));
        } finally {
            setEmployeesLoading(false);
        }
    };
    const closeOtDialog = () => { if (!otSaving) setOtDialog(false); };

    const saveOtEntry = async () => {
        setOtTried(true);
        const { employee, date, signOff } = otForm;
        if (!employee || !date || !signOff || date > todayIso()) return;
        const [yyyy, mm, dd] = date.split('-');
        setOtSaving(true);
        setOtError('');
        try {
            const { data: body } = await http.post(PostOvertimeEntry, {
                EmployeeCode: employee.employeeCode,
                Date: `${dd}-${mm}-${yyyy}`,
                SignOff: signOff,
            });
            if (body?.error) throw new Error(body.message || 'Could not add the overtime entry.');
            notify(body?.message || 'Overtime entry added and filed as Pending.');
            setOtDialog(false);
            loadDashboard();
        } catch (err) {
            // Server messages (400 no shift / within grace, 409 duplicate day) are shown in the dialog.
            setOtError(err?.response || err?.request ? apiErrorMessage(err, 'Could not add the overtime entry.') : err.message);
        } finally {
            setOtSaving(false);
        }
    };

    useEffect(() => { setGrace(String(dash.countsAfterMinutes)); }, [dash.countsAfterMinutes]);

    const { slabs, entries } = dash;

    const graceNum = Number(grace);
    const graceValid = grace !== '' && Number.isInteger(graceNum) && graceNum >= 0 && graceNum <= MAX_GRACE_MIN;
    const graceDirty = graceValid && graceNum !== dash.countsAfterMinutes;

    const setGraceDigits = (e) => {
        const digits = e.target.value.replace(/\D/g, '');
        if (digits === '') { setGrace(''); return; }
        const n = Number(digits);
        if (n > MAX_GRACE_MIN) return;
        setGrace(String(n));
    };

    const saveGrace = async () => {
        if (!graceDirty || savingGrace) return;
        setSavingGrace(true);
        try {
            const { data: body } = await http.put(UpdateOvertimeCountsAfter, { CountsAfterMinutes: graceNum });
            if (body?.error) throw new Error(body.message || 'Could not update the grace window.');
            notify(body?.message || `OT now counts after ${graceNum} min past shift end.`);
            await loadDashboard();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not update the grace window.'), 'error');
            setGrace(String(dash.countsAfterMinutes));
        } finally {
            setSavingGrace(false);
        }
    };

    const weekOffDirty = JSON.stringify(weekOff) !== JSON.stringify(savedWeekOff);
    const weekOffPay = weekOffPayOption(weekOff.payType);
    const saveWeekOff = async () => {
        if (savingWeekOff) return;
        if (weekOff.enabled && weekOff.payType === 'salarypct') {
            const r = Number(weekOff.rate);
            if (!(r >= MIN_OT_PCT && r <= MAX_OT_PCT)) {
                notify(`Sunday OT rate must be between ${MIN_OT_PCT}% and ${MAX_OT_PCT}% (100% = normal pay, 200% = double).`, 'warning');
                return;
            }
        }
        setSavingWeekOff(true);
        try {
            const payload = {
                Enabled: weekOff.enabled,
                PayBasis: WEEKOFF_TO_API[weekOff.payType] || 'slabs',
                Rate: ['salarypct', 'perhour'].includes(weekOff.payType) ? (Number(weekOff.rate) || 0) : 0,
            };
            const { data: body } = await http.put(UpdateSundayOvertime, payload);
            if (body?.error) throw new Error(body.message || 'Could not save the weekly-off OT setting.');
            notify(body?.message || 'Weekly-off overtime setting saved.');
            setSavedWeekOff(weekOff);
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the weekly-off OT setting.'), 'error');
        } finally {
            setSavingWeekOff(false);
        }
    };

    const openAdd = () => { setForm({ ...EMPTY_SLAB, fromHr: String(coverageEnd(slabs)) }); setTried(false); setDialog({}); };
    const openEdit = (s) => {
        setForm({
            label: s.label,
            fromHr: String(s.fromHr),
            toHr: s.toHr === null ? '' : String(s.toHr),
            andAbove: s.toHr === null,
            payType: s.payType,
            amount: String(s.amount),
        });
        setTried(false); setDialog({ id: s.id });
    };
    const closeDialog = () => { if (!saving) setDialog(null); };

    const fromN = Number(form.fromHr);
    const toN = Number(form.toHr);
    const amtN = Number(form.amount);

    const isPct = form.payType === 'salarypct';
    const isDaySalary = form.payType === 'halfdaysalary' || form.payType === 'fulldaysalary';
    const pctOutOfRange = isPct && (amtN < MIN_OT_PCT || amtN > MAX_OT_PCT);
    const amountValid = isDaySalary ? true : isPct ? (amtN >= MIN_OT_PCT && amtN <= MAX_OT_PCT) : amtN > 0;

    const editing = dialog?.id !== undefined && dialog?.id !== null;
    const others = dialog ? slabs.filter((s) => s.id !== dialog.id) : [];
    const nextBand = bandAbove(others, fromN);
    const blockedByOpenEnd = Boolean(dialog) && !editing && hasOpenEndedBand(others);
    const canBeOpenEnded = !nextBand;
    const maxTo = nextBand ? nextBand.fromHr : Infinity;

    const nameTaken = Boolean(form.label.trim())
        && others.some((s) => s.label.trim().toLowerCase() === form.label.trim().toLowerCase());
    const toTooHigh = !form.andAbove && form.toHr !== '' && toN > maxTo;

    const expectedFrom = editing
        ? (slabs.find((s) => s.id === dialog.id)?.fromHr ?? fromN)
        : coverageEnd(others);
    const fromWrong = Boolean(dialog) && fromN !== expectedFrom;

    const slabValid = Boolean(form.label.trim())
        && !nameTaken
        && !blockedByOpenEnd
        && !fromWrong
        && form.fromHr !== '' && !Number.isNaN(fromN) && fromN >= 0
        && (form.andAbove
            ? canBeOpenEnded
            : (form.toHr !== '' && toN > fromN && toN <= maxTo))
        && amountValid;

    const saveSlab = async () => {
        setTried(true);
        if (!slabValid) {
            notify(
                nameTaken ? 'Another slab already uses this name.'
                    : toTooHigh ? `This band overlaps "${nextBand.label}". Lower its "To" value.`
                        : blockedByOpenEnd ? 'Close off the open-ended band before adding another.'
                            : pctOutOfRange ? `OT rate must be between ${MIN_OT_PCT}% and ${MAX_OT_PCT}% of hourly pay (100% = normal, 200% = double).`
                                : 'Please complete the slab fields correctly.',
                'warning',
            );
            return;
        }

        const rateForPayload = form.payType === 'fulldaysalary' ? 1
            : form.payType === 'halfdaysalary' ? 0.5
                : amtN;

        const payload = {
            Name: form.label.trim(),
            FromHours: fromN,
            ToHours: form.andAbove ? null : toN,
            AndAbove: form.andAbove,
            PayType: OT_PAYTYPE_TO_API[form.payType] || 'perhour',
            Rate: rateForPayload,
        };

        setSaving(true);
        try {
            const editing = dialog?.id !== undefined && dialog?.id !== null;
            const { data: body } = editing
                ? await http.put(UpdateOvertimeRateSlab, { Id: dialog.id, ...payload })
                : await http.post(AddOvertimeRateSlab, payload);
            if (body?.error) throw new Error(body.message || 'Could not save the rate slab.');

            setDialog(null);
            notify(body?.message || (editing ? 'OT slab updated.' : 'OT slab added.'));
            await loadDashboard();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not save the rate slab.'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const deleteSlab = async (slab) => {
        setConfirm(null);
        setDeletingId(slab.id);
        try {
            const { data: body } = await http.delete(DeleteOvertimeRateSlab, { params: { id: slab.id } });
            if (body?.error) throw new Error(body.message || 'Could not delete the rate slab.');
            notify(body?.message || 'OT slab removed.');
            await loadDashboard();
        } catch (err) {
            notify(apiErrorMessage(err, 'Could not delete the rate slab.'), 'error');
        } finally {
            setDeletingId(null);
        }
    };

    const openReject = (entry) => { setRejectReason(''); setReject(entry); };

    // Approving a salary-based entry needs the Encryption Password; it is asked fresh and never kept.
    const [passPrompt, setPassPrompt] = useState(null); // { entry, error }

    const approve = (entry) => {
        if (entry.isSalaryBased) setPassPrompt({ entry, error: '' });
        else runEntryAction(entry, 'approve');
    };

    const runEntryAction = async (entry, action, reason = '', pass) => {
        if (!entry || actingId) return;
        setActingId(entry.id);
        try {
            const payload = { EntryId: entry.id, Action: action };
            if (action === 'reject') payload.Reason = reason.trim();

            const { data: body } = await http.put(UpdateOvertimeEntryAction, payload, pass ? passphraseConfig(pass) : undefined);
            if (body?.error) throw new Error(body.message || `Could not ${action} this entry.`);

            setReject(null);
            setPassPrompt(null);
            notify(body?.message || (action === 'approve'
                ? `OT approved — ${inr(Number(entry.otPay) || 0)} for ${entry.employeeName}.`
                : `OT entry from ${entry.employeeName} rejected.`));
            await loadDashboard();
        } catch (err) {
            if (pass && isPassphraseError(err)) setPassPrompt({ entry, error: passphraseErrorText(err) });
            else { setPassPrompt(null); notify(apiErrorMessage(err, `Could not ${action} this entry.`), 'error'); }
        } finally {
            setActingId(null);
        }
    };

    const set =(k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const sampleMonthlyN = SAMPLE_MONTHLY;
    const sampleHourly = hourlyFromMonthly(sampleMonthlyN);
    const sampleDaily = dailyFromMonthly(sampleMonthlyN);
    const previewHrs = form.andAbove ? fromN + 1 : (toN || fromN);
    const previewPay = slabPayAt({ payType: form.payType, amount: amtN }, previewHrs || 0, sampleMonthlyN);

    // Reload, Add OT entry and Add Rate Slab sit at the right end of the breadcrumb row. Handlers go through a ref so
    // the buttons always call the latest ones without being rebuilt on every render.
    const setCrumbActions = useCrumbActionsSetter();
    const actionsRef = useRef({});
    actionsRef.current = { loadDashboard, openOtDialog, openAdd };
    useEffect(() => {
        if (!setCrumbActions) return undefined;
        setCrumbActions(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Tooltip arrow title="Reload">
                    <IconButton onClick={() => actionsRef.current.loadDashboard()} sx={{ border: '1px solid #E6EAF1', borderRadius: '7px', bgcolor: '#fff', color: '#64748B', width: 42, height: 42, '&:hover': { bgcolor: PRIMARY_LIGHT, color: PRIMARY } }}>
                        <RefreshRoundedIcon sx={{ fontSize: 19 }} />
                    </IconButton>
                </Tooltip>
                <Button startIcon={<AccessTimeFilledRoundedIcon sx={{ fontSize: 18 }} />} onClick={() => actionsRef.current.openOtDialog()} sx={{ ...solidBtn, height: 42, px: 2.2, fontSize: 13 }}>Add OT entry</Button>
                <Button startIcon={<AddRoundedIcon sx={{ fontSize: 18 }} />} onClick={() => actionsRef.current.openAdd()} sx={{ ...solidBtn, height: 42, px: 2.2, fontSize: 13 }}>Add Rate Slab</Button>
            </Box>,
        );
        return () => setCrumbActions(null);
    }, [setCrumbActions]);

    if (loading) {
        return (
            <Box sx={{ height: '70vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <LinearProgress sx={{ width: 300 }} />
            </Box>
        );
    }

    return (
        <Box sx={{ px: { xs: 1.5, md: 2.5 }, pt: 2.5, pb: 3 }}>
            {loadError && (
                <LoadAlert sx={{ mb: 1.5, borderRadius: '7px' }}
                    action={<Button size="small" onClick={loadDashboard} sx={{ textTransform: 'none', fontWeight: 700 }}>Retry</Button>}>
                    {loadError}
                </LoadAlert>
            )}

            <Box sx={{ ...card, p: 0, overflow: 'hidden', mb: 2.5 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 2.5, py: 1.8, bgcolor: '#fff', borderBottom: weekOff.enabled ? '1px solid #EAE7F7' : 'none', flexWrap: 'wrap' }}>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Sunday Overtime</Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>Work on a Sunday counts as overtime, paid by the rule you set.</Typography>
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 'auto' }}>
                        <Button
                            onClick={saveWeekOff}
                            disabled={savingWeekOff || !weekOffDirty}
                            startIcon={savingWeekOff ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : null}
                            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12.5, height: 32, px: 2.4, minWidth: 82, borderRadius: '7px', color: '#fff', bgcolor: '#16A34A', boxShadow: 'none', '&:hover': { bgcolor: '#15803D' }, '&.Mui-disabled': { bgcolor: '#CDE9D6', color: '#fff' } }}
                        >
                            {savingWeekOff ? 'Saving…' : weekOffDirty ? 'Save' : 'Saved'}
                        </Button>
                        <Switch
                            checked={weekOff.enabled}
                            onChange={(e) => setWeekOff((w) => ({ ...w, enabled: e.target.checked }))}
                            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: PRIMARY }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: PRIMARY } }}
                        />
                    </Stack>
                </Stack>

                {weekOff.enabled && (
                    <Box sx={{ p: 1.8 }}>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 0.8 }}>How Sunday overtime is paid</Typography>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <TextField select size="small" value={weekOff.payType}
                                onChange={(e) => setWeekOff((w) => ({ ...w, payType: e.target.value }))}
                                sx={{ minWidth: 230 }}>
                                {WEEKOFF_PAY_OPTIONS.map((o) => <MenuItem key={o.key} value={o.key} sx={{ fontSize: 13.5 }}>{o.label}</MenuItem>)}
                            </TextField>
                            {!weekOffPay.noValue && (
                                <TextField
                                    size="small" type="number" label={weekOffPay.key === 'salarypct' ? '% of hourly' : 'Rate per hour'}
                                    value={weekOff.rate}
                                    onChange={(e) => setWeekOff((w) => ({ ...w, rate: e.target.value }))}
                                    error={weekOffPay.key === 'salarypct' && (Number(weekOff.rate) < MIN_OT_PCT || Number(weekOff.rate) > MAX_OT_PCT)}
                                    sx={{ width: 150 }}
                                    InputProps={weekOffPay.prefix
                                        ? { startAdornment: <InputAdornment position="start">₹</InputAdornment> }
                                        : { endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                                    inputProps={weekOffPay.key === 'salarypct' ? { min: MIN_OT_PCT, max: MAX_OT_PCT } : { min: 0 }}
                                />
                            )}
                            {weekOffPay.key === 'salarypct' && (
                                <Typography sx={{ fontSize: 11, color: (Number(weekOff.rate) < MIN_OT_PCT || Number(weekOff.rate) > MAX_OT_PCT) ? '#E11D48' : '#98A0AE' }}>
                                    {MIN_OT_PCT}–{MAX_OT_PCT}% (100% = normal, 200% = double)
                                </Typography>
                            )}
                        </Stack>

                        <Box sx={{ mt: 1.4, p: 1.4, borderRadius: '9px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                            <Typography sx={{ fontSize: 12.5, color: '#166534' }}>
                                Staff who work on <strong>Sunday</strong> earn overtime{' '}
                                {weekOff.payType === 'slabs'
                                    ? <>via the <strong>OT rate slabs</strong> below.</>
                                    : weekOff.payType === 'salarypct'
                                        ? <>at <strong>{Number(weekOff.rate) || 0}%</strong> of their hourly salary.</>
                                        : weekOff.payType === 'perhour'
                                            ? <>at <strong>{inr(Number(weekOff.rate) || 0)}/hour</strong>.</>
                                            : <>of a <strong>{weekOff.payType === 'fulldaysalary' ? 'full' : 'half'} day&apos;s salary</strong>.</>}
                            </Typography>
                            {['salarypct', 'fulldaysalary', 'halfdaysalary'].includes(weekOff.payType) && (
                                <Typography sx={{ fontSize: 11, color: '#3F8A5C', mt: 0.6, pt: 0.6, borderTop: '1px dashed #BBF7D0' }}>
                                    <Box component="span" sx={{ fontWeight: 800 }}>Example</Box> — on a {inr(SAMPLE_MONTHLY)}/month salary
                                    {weekOff.payType === 'salarypct'
                                        ? <>, one hour ≈ <strong>₹{Math.round(hourlyFromMonthly(SAMPLE_MONTHLY)).toLocaleString('en-IN')}</strong>, so {Number(weekOff.rate) || 0}% ≈ <strong>₹{Math.round(hourlyFromMonthly(SAMPLE_MONTHLY) * (Number(weekOff.rate) || 0) / 100).toLocaleString('en-IN')}/hour</strong>.</>
                                        : weekOff.payType === 'fulldaysalary'
                                            ? <>, a full day ≈ <strong>₹{Math.round(dailyFromMonthly(SAMPLE_MONTHLY)).toLocaleString('en-IN')}</strong>.</>
                                            : <>, half a day ≈ <strong>₹{Math.round(dailyFromMonthly(SAMPLE_MONTHLY) / 2).toLocaleString('en-IN')}</strong>.</>}
                                    {' '}Actual pay uses each employee&apos;s own salary.
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}
            </Box>

            <Box sx={{ ...card, p: 0, overflow: 'hidden', mb: 2.5 }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 2.5, py: 1.8, bgcolor: '#fff', borderBottom: '1px solid #EAE7F7', flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>OT Rate Slabs</Typography>
                    <Chip label={`${slabs.length} bands`} size="small" sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 11.5 }} />
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center', ml: 'auto' }}>
                        <TuneRoundedIcon sx={{ fontSize: 17, color: '#94A3B8' }} />
                        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>OT counts after</Typography>
                        <TextField
                            size="small" value={grace} onChange={setGraceDigits} disabled={savingGrace}
                            onKeyDown={(e) => { if (e.key === 'Enter') saveGrace(); }}
                            error={!graceValid}
                            inputProps={{ inputMode: 'numeric', maxLength: 3, 'aria-label': 'OT grace minutes' }}
                            sx={{ width: 88, '& .MuiOutlinedInput-root': { borderRadius: '7px', bgcolor: '#fff', height: 34, fontSize: 13, fontWeight: 700 } }}
                            slotProps={{ input: { endAdornment: <InputAdornment position="end"><Typography sx={{ fontSize: 11, color: '#94A3B8' }}>min</Typography></InputAdornment> } }}
                        />
                        <Typography sx={{ fontSize: 12, color: '#6B7280' }}>past shift end</Typography>
                        {graceDirty && (
                            <Tooltip arrow title={`Save — OT will count after ${graceNum} min`}>
                                <span>
                                    <IconButton onClick={saveGrace} disabled={savingGrace} size="small"
                                        sx={{ color: '#fff', bgcolor: '#16A34A', width: 30, height: 30, '&:hover': { bgcolor: '#15803D' }, '&.Mui-disabled': { bgcolor: '#A7F3D0', color: '#fff' } }}>
                                        {savingGrace ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CheckRoundedIcon sx={{ fontSize: 17 }} />}
                                    </IconButton>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                </Stack>

                <Box sx={{ px: 2, py: 1, bgcolor: '#FFFBEB', borderBottom: '1px solid #FDE68A', display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                    <AccessTimeFilledRoundedIcon sx={{ fontSize: 16, color: '#B45309', mt: 0.1 }} />
                    <Typography sx={{ fontSize: 11.5, color: '#92400E', lineHeight: 1.6 }}>
                        The slab is picked by the <strong>total OT duration</strong>. Each band pays a <strong>flat ₹/hour</strong>, a <strong>flat amount</strong>,
                        a <strong>% of hourly salary</strong>, or a <strong>half / full day&apos;s salary</strong> — the salary-based ones keep OT fair for higher earners. Add as many bands as you need.
                    </Typography>
                </Box>

                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 760, borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                            <Box component="tr">
                                {['SLAB', 'OT DURATION', 'PAY TYPE', 'RATE / AMOUNT', 'EXAMPLE PAYOUT', 'ACTIONS'].map((h) => (
                                    <Box component="th" key={h} sx={{ textAlign: h === 'ACTIONS' ? 'right' : 'left', px: 2, py: 1.15, fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6, borderBottom: '1px solid #E8E6F3', whiteSpace: 'nowrap' }}>{h}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {slabs.map((s, idx) => {
                                const sample = s.toHr === null ? s.fromHr + 2 : s.toHr;
                                const busy = deletingId === s.id;
                                return (
                                    <Box component="tr" key={s.id ?? idx} sx={{ bgcolor: idx % 2 ? '#FBFAFE' : '#fff', opacity: busy ? 0.5 : 1 }}>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Stack direction="row" spacing={1.2} sx={{ alignItems: 'center' }}>
                                                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: PRIMARY_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: PRIMARY_DARK }}>{idx + 1}</Box>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{s.label}</Typography>
                                            </Stack>
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Chip label={slabRange(s)} size="small" sx={{ bgcolor: '#E0F2FE', color: '#0369A1', fontWeight: 700, fontSize: 11.5 }} />
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Chip label={payMeta(s.payType).label} size="small"
                                                sx={{ fontWeight: 700, fontSize: 11.5, bgcolor: payMeta(s.payType).bg, color: payMeta(s.payType).color }} />
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            {s.payType === 'salarypct' ? (
                                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{s.amount}%<Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE' }}> of hourly</Box></Typography>
                                            ) : s.payType === 'fulldaysalary' ? (
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0F172A' }}>1 day&apos;s<Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE' }}> salary</Box></Typography>
                                            ) : s.payType === 'halfdaysalary' ? (
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#0F172A' }}>½ day&apos;s<Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE' }}> salary</Box></Typography>
                                            ) : (
                                                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>{inr(s.amount)}<Box component="span" sx={{ fontSize: 11, fontWeight: 600, color: '#98A0AE' }}>{s.payType === 'flat' ? ' total' : ' /hr'}</Box></Typography>
                                            )}
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            {s.payType === 'salarypct' ? (
                                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{fmtHrs(sample)} → <strong style={{ color: '#16A34A' }}>{s.amount}% × pay</strong></Typography>
                                            ) : s.payType === 'fulldaysalary' ? (
                                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{fmtHrs(sample)} → <strong style={{ color: '#16A34A' }}>1 day&apos;s pay</strong></Typography>
                                            ) : s.payType === 'halfdaysalary' ? (
                                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{fmtHrs(sample)} → <strong style={{ color: '#16A34A' }}>½ day&apos;s pay</strong></Typography>
                                            ) : (
                                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>{fmtHrs(sample)} → <strong style={{ color: '#16A34A' }}>{inr(slabPayAt(s, sample))}</strong></Typography>
                                            )}
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            <Tooltip arrow title="Edit slab">
                                                <span>
                                                    <IconButton size="small" disabled={busy} onClick={() => openEdit(s)} sx={{ color: PRIMARY, '&:hover': { bgcolor: PRIMARY_LIGHT } }}><EditRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
                                                </span>
                                            </Tooltip>
                                            <Tooltip arrow title="Delete slab">
                                                <span>
                                                    <IconButton size="small" disabled={busy} onClick={() => setConfirm(s)} sx={{ ml: 0.5, color: '#98A0AE', '&:hover': { color: '#E11D48', bgcolor: '#FEF2F2' } }}>
                                                        {busy ? <CircularProgress size={15} /> : <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />}
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    {slabs.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <LayersRoundedIcon sx={{ fontSize: 34, color: '#CBD2DD' }} />
                            <Typography sx={{ fontSize: 14, color: '#98A0AE', mt: 1 }}>No slabs yet. Add one with "Add Rate Slab".</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 2.5, py: 1.8, bgcolor: '#fff', borderBottom: '1px solid #EAE7F7' }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0F172A' }}>Overtime Entries</Typography>
                    <Chip label={`${dash.pendingApprovals} pending`} size="small" sx={{ bgcolor: '#FFF7ED', color: '#B45309', fontWeight: 700, fontSize: 11.5 }} />
                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE', ml: 'auto', display: { xs: 'none', sm: 'block' } }}>Derived from sign-off time vs scheduled shift end</Typography>
                </Stack>
                <Box sx={{ overflowX: 'auto' }}>
                    <Box component="table" sx={{ width: '100%', minWidth: 1040, borderCollapse: 'collapse' }}>
                        <Box component="thead" sx={{ bgcolor: '#F4F3FB' }}>
                            <Box component="tr">
                                {['EMPLOYEE', 'DATE', 'SHIFT END → SIGN OFF', 'OT HOURS', 'SLAB', 'OT PAY', 'STATUS', 'ACTIONS'].map((h) => (
                                    <Box component="th" key={h} sx={{ textAlign: h === 'ACTIONS' ? 'right' : 'left', px: 2, py: 1.15, fontSize: 10.5, fontWeight: 700, color: '#6E6B99', letterSpacing: 0.6, borderBottom: '1px solid #E8E6F3', whiteSpace: 'nowrap' }}>{h}</Box>
                                ))}
                            </Box>
                        </Box>
                        <Box component="tbody">
                            {entries.map((r, idx) => {
                                const approved = r.status === 'approved';
                                const rejected = r.status === 'rejected';
                                const pending = !approved && !rejected;
                                const busy = actingId === r.id;
                                const acting = Boolean(actingId);
                                return (
                                    <Box component="tr" key={r.id ?? idx} sx={{ bgcolor: idx % 2 ? '#FBFAFE' : '#fff' }}>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Stack direction="row" spacing={1.4} sx={{ alignItems: 'center' }}>
                                                <Avatar sx={{ width: 34, height: 34, ...avatarTone(r.employeeName), fontSize: 12.5, fontWeight: 700 }}>{initials(r.employeeName)}</Avatar>
                                                <Box>
                                                    <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: '#0F172A' }}>{cleanName(r.employeeName)}</Typography>
                                                    <Typography sx={{ fontSize: 11.5, color: '#98A0AE' }}>{r.employeeCode} · {r.department}</Typography>
                                                </Box>
                                            </Stack>
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6', fontSize: 12.5, color: '#475569', whiteSpace: 'nowrap' }}>{r.date || '—'}</Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center' }}>
                                                <Chip label={r.shiftEnd || '—'} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: '#F1F5F9', color: '#475569' }} />
                                                <LoginRoundedIcon sx={{ fontSize: 14, color: '#CBD2DD' }} />
                                                <Chip label={r.signOff || '—'} size="small" sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: PRIMARY_LIGHT, color: PRIMARY_DARK }} />
                                            </Stack>
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0EA5E9' }}>{fmtHrs(r.otHours)}</Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            {r.slabName
                                                ? <Chip label={r.slabName} size="small" sx={{ bgcolor: '#E0F2FE', color: '#0369A1', fontWeight: 700, fontSize: 11 }} />
                                                : <Tooltip arrow title="No rate slab covers this OT duration"><Typography sx={{ fontSize: 12, color: '#E11D48' }}>No slab</Typography></Tooltip>}
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#16A34A' }}>{inr(r.otPay)}</Typography>
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6' }}>
                                            <Tooltip arrow title={rejected && r.rejectReason ? r.rejectReason : ''}>
                                                <Chip
                                                    label={approved ? 'Approved' : rejected ? 'Rejected' : 'Pending'}
                                                    size="small"
                                                    sx={{
                                                        height: 22, fontSize: 11, fontWeight: 700,
                                                        bgcolor: approved ? '#DCFCE7' : rejected ? '#FEE2E2' : '#FEF3C7',
                                                        color: approved ? '#16A34A' : rejected ? '#E11D48' : '#B45309',
                                                    }}
                                                />
                                            </Tooltip>
                                        </Box>
                                        <Box component="td" sx={{ px: 2, py: 1.15, borderBottom: '1px solid #EEF0F6', textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {pending ? (
                                                <Stack direction="row" spacing={0.8} sx={{ justifyContent: 'flex-end' }}>
                                                    <Button
                                                        onClick={() => approve(r)}
                                                        disabled={acting}
                                                        startIcon={busy ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <CheckRoundedIcon />}
                                                        sx={{ ...solidBtn, height: 32, px: 1.5, fontSize: 12, bgcolor: '#16A34A', '&:hover': { bgcolor: '#15803D' }, '&.Mui-disabled': { bgcolor: '#A7F3D0', color: '#fff' } }}>
                                                        Approve
                                                    </Button>
                                                    <Tooltip arrow title="Reject with a reason">
                                                        <span>
                                                            <IconButton onClick={() => openReject(r)} disabled={acting} size="small"
                                                                sx={{ color: '#E11D48', border: '1px solid #FBCFE8', borderRadius: '7px', width: 32, height: 32, '&:hover': { bgcolor: '#FEF2F2' } }}>
                                                                <CloseRoundedIcon sx={{ fontSize: 17 }} />
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                </Stack>
                                            ) : (
                                                <Typography sx={{ fontSize: 11.5, color: '#CBD2DD' }}>—</Typography>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                    {entries.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 6 }}>
                            <AccessTimeFilledRoundedIcon sx={{ fontSize: 34, color: '#CBD2DD' }} />
                            <Typography sx={{ fontSize: 14, color: '#98A0AE', mt: 1 }}>No overtime entries for this period.</Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            <Dialog open={Boolean(dialog)} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>{dialog?.id ? 'Edit Rate Slab' : 'Add Rate Slab'}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>Define an OT-duration band and how it is paid.</Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    {blockedByOpenEnd && (
                        <Alert severity="warning" sx={{ mb: 2, borderRadius: '9px', fontSize: 12.5, py: 0.5 }}>
                            The top band has no upper limit, so it already covers every OT duration above it.
                            Give it a fixed "To" value first, then add the next band.
                        </Alert>
                    )}

                    <TextField label="Slab name" size="small" fullWidth value={form.label} onChange={set('label')}
                        error={(tried && !form.label.trim()) || nameTaken}
                        helperText={nameTaken ? `A slab named "${form.label.trim()}" already exists.` : ' '}
                        placeholder="e.g. Extended OT" sx={{ mb: 1 }} />

                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: '#374151', mb: 1 }}>OT duration band (in hours)</Typography>
                    <Stack direction="row" spacing={1.2} sx={{ mb: 1 }}>
                        <Tooltip arrow title={editing ? "Fixed by the band below this one" : 'A new band starts where the last one ends'}>
                            <TextField label="From" size="small" fullWidth disabled value={form.fromHr}
                                InputProps={{ endAdornment: <InputAdornment position="end">hr</InputAdornment> }} />
                        </Tooltip>
                        <TextField label="To" type="number" size="small" fullWidth value={form.toHr} onChange={set('toHr')} disabled={form.andAbove || blockedByOpenEnd}
                            error={(tried && !form.andAbove && (form.toHr === '' || toN <= fromN)) || toTooHigh}
                            InputProps={{ endAdornment: <InputAdornment position="end">hr</InputAdornment> }} />
                    </Stack>
                    <Typography sx={{ fontSize: 11, color: toTooHigh ? '#E11D48' : '#98A0AE', mb: 1.2 }}>
                        {toTooHigh
                            ? `Must end at ${maxTo} hr or below — the "${nextBand.label}" band starts there.`
                            : editing
                                ? `This band starts at ${fromN} hr and cannot move${nextBand ? `; it must end by ${maxTo} hr, where "${nextBand.label}" begins.` : '.'}`
                                : fromN > 0
                                    ? `Bands can't overlap, so this one picks up at ${fromN} hr where the last band ends.`
                                    : 'This is the first band, so it starts at 0.'}
                    </Typography>

                    <Tooltip arrow title={canBeOpenEnded ? '' : `Only the topmost band can be open-ended — "${nextBand?.label}" sits above this one.`}>
                        <Box component="span" sx={{ display: 'inline-block', mb: 2 }}>
                            <Box
                                onClick={() => { if (canBeOpenEnded && !blockedByOpenEnd) setForm((f) => ({ ...f, andAbove: !f.andAbove })); }}
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, cursor: canBeOpenEnded && !blockedByOpenEnd ? 'pointer' : 'not-allowed', opacity: canBeOpenEnded && !blockedByOpenEnd ? 1 : 0.5 }}>
                                <Box sx={{ width: 18, height: 18, borderRadius: '5px', border: `1.5px solid ${form.andAbove ? PRIMARY : '#CBD5E1'}`, bgcolor: form.andAbove ? PRIMARY : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {form.andAbove && <CheckRoundedIcon sx={{ fontSize: 13, color: '#fff' }} />}
                                </Box>
                                <Typography sx={{ fontSize: 12.5, color: '#475569' }}>and above (no upper limit)</Typography>
                            </Box>
                        </Box>
                    </Tooltip>

                    <Stack direction="row" spacing={1.2} sx={{ mb: (isPct || isDaySalary) ? 1.2 : 2 }}>
                        <TextField select label="Pay basis" size="small" fullWidth value={form.payType} onChange={set('payType')}>
                            <MenuItem value="perhour">Per hour (₹ × OT hours)</MenuItem>
                            <MenuItem value="flat">Flat amount (one lump sum)</MenuItem>
                            <MenuItem value="salarypct">% of the employee&apos;s hourly salary</MenuItem>
                            <MenuItem value="halfdaysalary">Half-day salary</MenuItem>
                            <MenuItem value="fulldaysalary">Full-day salary</MenuItem>
                        </TextField>
                        {isDaySalary ? (
                            <TextField label="Pays" size="small" fullWidth disabled
                                value={form.payType === 'fulldaysalary' ? "1 day's salary" : "½ day's salary"} />
                        ) : (
                            <TextField
                                label={isPct ? '% of hourly pay' : form.payType === 'flat' ? 'Flat amount' : 'Rate per hour'}
                                type="number" size="small" fullWidth value={form.amount} onChange={set('amount')}
                                error={tried && !amountValid}
                                helperText={isPct ? (pctOutOfRange ? `${MIN_OT_PCT}–${MAX_OT_PCT}% only` : `${MIN_OT_PCT}–${MAX_OT_PCT}% · 100%=normal, 200%=double`) : ' '}
                                InputProps={isPct
                                    ? { endAdornment: <InputAdornment position="end">%</InputAdornment> }
                                    : { startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                inputProps={isPct ? { min: MIN_OT_PCT, max: MAX_OT_PCT } : undefined} />
                        )}
                    </Stack>

                    {(isPct || isDaySalary) && (
                        <Box sx={{ mb: 2 }}>
                            {isPct && (
                                <Stack direction="row" spacing={0.8} sx={{ mb: 1.2, flexWrap: 'wrap', gap: 0.8 }}>
                                    {[100, 150, 200, 300].map((p) => (
                                        <Chip key={p} label={`${p}% · ${p / 100}× pay`} size="small" onClick={() => setForm((f) => ({ ...f, amount: String(p) }))}
                                            sx={{ cursor: 'pointer', fontWeight: 700, fontSize: 11.5, bgcolor: amtN === p ? PRIMARY : '#F1F5F9', color: amtN === p ? '#fff' : '#475569', '&:hover': { bgcolor: amtN === p ? PRIMARY_DARK : '#E2E8F0' } }} />
                                    ))}
                                </Stack>
                            )}
                            <Box sx={{ p: 1.2, borderRadius: '7px', bgcolor: '#F8FAFC', border: '1px solid #EEF1F6' }}>
                                <Typography sx={{ fontSize: 11, color: '#6B7280', lineHeight: 1.55 }}>
                                    <Box component="span" sx={{ fontWeight: 800, color: '#475569' }}>Example only.</Box>{' '}
                                    {isPct
                                        ? <>On a sample <strong>{inr(SAMPLE_MONTHLY)}</strong>/month salary, one hour ≈ <strong>₹{Math.round(sampleHourly).toLocaleString('en-IN')}</strong> (salary ÷ {STD_MONTHLY_HOURS} hrs).</>
                                        : <>On a sample <strong>{inr(SAMPLE_MONTHLY)}</strong>/month salary, one day ≈ <strong>₹{Math.round(sampleDaily).toLocaleString('en-IN')}</strong> (salary ÷ {STD_WORKING_DAYS} days).</>}
                                    {' '}The actual OT is calculated from each employee&apos;s own salary at payroll.
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {slabValid && (
                        <Box sx={{ p: 1.6, borderRadius: '9px', bgcolor: '#F0FDF4', border: '1px solid #BBF7D0' }}>
                            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#15803D', textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.4 }}>Preview</Typography>
                            <Typography sx={{ fontSize: 12.5, color: '#166534' }}>
                                An employee doing <strong>{fmtHrs(previewHrs)}</strong> of OT
                                {form.payType === 'flat'
                                    ? <> is paid a flat <strong>{inr(amtN)}</strong>.</>
                                    : isDaySalary
                                        ? <> on a <strong>{inr(sampleMonthlyN)}</strong> salary earns <strong>{inr(previewPay)}</strong> — {form.payType === 'fulldaysalary' ? "a full day's" : "half a day's"} pay (day ≈ {inr(Math.round(sampleDaily))}).</>
                                        : isPct
                                            ? <> on a <strong>{inr(sampleMonthlyN)}</strong> salary earns <strong>{inr(previewPay)}</strong> — {amtN}% of their ≈{inr(Math.round(sampleHourly))}/hr rate.</>
                                            : <> earns <strong>{inr(previewPay)}</strong> ({inr(amtN)}/hr).</>}
                            </Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeDialog} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748B', borderRadius: '7px' }}>Cancel</Button>
                    <Button onClick={saveSlab} disabled={saving || blockedByOpenEnd} startIcon={saving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {saving ? 'Saving…' : dialog?.id ? 'Save Slab' : 'Add Slab'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={otDialog} onClose={closeOtDialog} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Add OT entry</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        Enter the sign-off time. Shift end comes from the employee&apos;s assigned shift, and the entry is filed as Pending.
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    {otError && <Alert severity="error" sx={{ mb: 2, borderRadius: '9px', fontSize: 12.5, py: 0.5 }}>{otError}</Alert>}
                    <Stack spacing={2}>
                        <Autocomplete
                            size="small"
                            options={employees}
                            loading={employeesLoading}
                            value={otForm.employee}
                            onChange={(_, v) => setOtForm((f) => ({ ...f, employee: v }))}
                            getOptionLabel={(o) => `${cleanName(o.fullName)} (${o.employeeCode})`}
                            isOptionEqualToValue={(a, b) => a.employeeCode === b.employeeCode}
                            renderInput={(params) => (
                                <TextField {...params} label="Employee" error={otTried && !otForm.employee} helperText={otTried && !otForm.employee ? 'Pick an employee' : ''} />
                            )}
                        />
                        <TextField
                            label="Date" type="date" size="small" fullWidth
                            value={otForm.date} onChange={(e) => setOtForm((f) => ({ ...f, date: e.target.value }))}
                            slotProps={{ inputLabel: { shrink: true }, htmlInput: { max: todayIso() } }}
                            error={otTried && (!otForm.date || otForm.date > todayIso())}
                            helperText={otTried && (!otForm.date || otForm.date > todayIso()) ? 'Pick today or an earlier date' : ''}
                        />
                        <TextField
                            label="Sign-off time" type="time" size="small" fullWidth
                            value={otForm.signOff} onChange={(e) => setOtForm((f) => ({ ...f, signOff: e.target.value }))}
                            slotProps={{ inputLabel: { shrink: true } }}
                            error={otTried && !otForm.signOff}
                            helperText={otTried && !otForm.signOff ? 'Enter the sign-off time' : 'A sign-off after midnight is handled'}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={closeOtDialog} disabled={otSaving} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748B', borderRadius: '7px' }}>Cancel</Button>
                    <Button onClick={saveOtEntry} disabled={otSaving} startIcon={otSaving ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : null} sx={{ ...solidBtn, height: 40, px: 2.4 }}>
                        {otSaving ? 'Adding…' : 'Add entry'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(confirm)} onClose={() => setConfirm(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, color: '#0F172A' }}>Delete this rate slab?</Typography>
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ fontSize: 13, color: '#475569' }}>
                        <strong>{confirm?.label}</strong> ({confirm ? slabRange(confirm) : ''}) will be removed permanently.
                        OT falling in this band will have no slab until another one covers it.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setConfirm(null)} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748B', borderRadius: '7px' }}>Cancel</Button>
                    <Button onClick={() => deleteSlab(confirm)} sx={{ ...solidBtn, height: 38, px: 2.2, bgcolor: '#E11D48', '&:hover': { bgcolor: '#BE123C' } }}>Delete</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={Boolean(reject)} onClose={() => { if (!actingId) setReject(null); }} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '12px' } }}>
                <DialogTitle sx={{ pb: 1 }}>
                    <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#0F172A' }}>Reject Overtime Entry</Typography>
                    <Typography sx={{ fontSize: 12.5, color: '#6B7280', mt: 0.2 }}>
                        {reject ? `${reject.employeeName} · ${reject.date} · ${fmtHrs(reject.otHours)}` : ''} — the employee sees this reason, so say what they should do next.
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: '8px !important' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#374151', mb: 1 }}>Common reasons — tap to use, then edit if needed</Typography>
                    <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.8, mb: 2 }}>
                        {OT_REJECT_TEMPLATES.map((t) => {
                            const active = rejectReason === t.text;
                            return (
                                <Chip
                                    key={t.label} label={t.label} size="small" onClick={() => setRejectReason(t.text)}
                                    sx={{
                                        cursor: 'pointer', fontWeight: 700, fontSize: 11.5,
                                        bgcolor: active ? PRIMARY : '#F1F5F9', color: active ? '#fff' : '#475569',
                                        '&:hover': { bgcolor: active ? PRIMARY_DARK : '#E2E8F0' },
                                    }}
                                />
                            );
                        })}
                    </Stack>
                    <TextField
                        label="Rejection reason" size="small" fullWidth multiline minRows={3}
                        value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
                        placeholder="Pick a reason above, or write your own."
                        helperText={`${rejectReason.trim().length}/400`}
                        inputProps={{ maxLength: 400 }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setReject(null)} disabled={Boolean(actingId)} sx={{ textTransform: 'none', fontWeight: 700, color: '#64748B', borderRadius: '7px' }}>Cancel</Button>
                    <Button
                        onClick={() => runEntryAction(reject, 'reject', rejectReason)}
                        disabled={Boolean(actingId) || !rejectReason.trim()}
                        startIcon={actingId ? <CircularProgress size={15} sx={{ color: '#fff' }} /> : <CloseRoundedIcon />}
                        sx={{ ...solidBtn, height: 40, px: 2.4, bgcolor: '#E11D48', '&:hover': { bgcolor: '#BE123C' }, '&.Mui-disabled': { bgcolor: '#FBCFE8', color: '#fff' } }}>
                        {actingId ? 'Rejecting…' : 'Reject Entry'}
                    </Button>
                </DialogActions>
            </Dialog>

            <SecretDialog
                open={Boolean(passPrompt)}
                title="Encryption Password"
                subtitle={passPrompt ? `Approving ${passPrompt.entry.employeeName}'s OT needs their real salary.` : ''}
                fields={[{ key: 'pass', label: 'Encryption Password' }]}
                submitLabel="Approve"
                busy={Boolean(actingId)}
                error={passPrompt?.error}
                onClose={() => setPassPrompt(null)}
                onSubmit={({ pass }) => runEntryAction(passPrompt.entry, 'approve', '', pass)}
            />

            <Snackbar open={Boolean(snack)} autoHideDuration={3800} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'success'} variant="filled" sx={{ borderRadius: '7px' }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
